import { useCallback, useEffect, useState } from 'react';

const prefix = 'mio.field.tour.v2.';
type TourStatus = 'started' | 'dismissed' | 'completed';

function wasOffered(engagementId: string | undefined) {
  if (!engagementId) return false;
  try {
    return ['started', 'dismissed', 'completed'].includes(
      localStorage.getItem(`${prefix}${engagementId}`) ?? '',
    );
  } catch {
    return false;
  }
}

function initialState(engagementId: string | undefined) {
  return { engagementId, step: null as number | null, offered: wasOffered(engagementId) };
}

/** Only the tour preference is stored, scoped to this engagement and tour version. */
export function useFieldTour(engagementId: string | undefined) {
  const [state, setState] = useState(() => initialState(engagementId));
  useEffect(() => {
    setState(initialState(engagementId));
  }, [engagementId]);

  const remember = useCallback(
    (status: TourStatus, step: number | null) => {
      if (!engagementId) return;
      setState({ engagementId, step, offered: true });
      try {
        localStorage.setItem(`${prefix}${engagementId}`, status);
      } catch {
        // The tour remains usable for this visit when browser storage is unavailable.
      }
    },
    [engagementId],
  );
  const goTo = useCallback((step: number) => remember('started', step), [remember]);
  const dismiss = useCallback(() => remember('dismissed', null), [remember]);
  const complete = useCallback(() => remember('completed', null), [remember]);
  const current = state.engagementId === engagementId;
  return {
    step: current ? state.step : null,
    showInvitation: Boolean(engagementId && current && !state.offered),
    goTo,
    dismiss,
    complete,
  };
}
